/**
 * PraticheQuotidianeTabRebuilt — Lavoro Quotidiano versione
 * COMPLETA ricostruita da SafeMode (22/02/2026).
 *
 * Sostituisce il vecchio `PraticheQuotidianeTabFull` come DEFAULT.
 * Mantiene tutte le funzionalità di scrittura (creazione, modifica,
 * conclusione, nota rapida, stampa singola) ma evita
 * categoricamente ogni primitiva potenzialmente coinvolta nei
 * crash React 19 `insertBefore` / `removeChild`:
 *
 *   ❌ Nessun Radix `<Select>` (portal) → `<select>` HTML nativo
 *   ❌ Nessun Radix `<Dialog>` (portal) → modale CSS inline con
 *      backdrop (no `createPortal`, no `Presence`)
 *   ❌ Nessun Radix `<Tabs>` (già rimosso dal path a App.js)
 *   ❌ Nessun `<Card>` shadcn → `<div>` con bordo
 *   ❌ Nessun toast (Sonner) durante render → toast solo dopo
 *      successful API response, in setTimeout(0) per uscire dal
 *      batch React
 *   ❌ Nessun useDeferredValue / startTransition
 *   ❌ Nessun React.memo (potenziale fonte di stale closures)
 *   ❌ Nessun Fragment sotto tbody
 *   ❌ Empty-state strutturalmente stabile (tbody con tr hidden)
 *   ✅ Solo HTML nativo + React state + sonner per notifiche
 *      transient (post-success)
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/api";
import { AREA_OPTIONS, getAreaLabel } from "@/lib/areaOptions";

const MGMT_OPTIONS = [
  { value: "agenzia", label: "Agenzia" },
  { value: "studio", label: "Studio" },
];

const MONTHS_IT = [
  ["01", "Gennaio"], ["02", "Febbraio"], ["03", "Marzo"], ["04", "Aprile"],
  ["05", "Maggio"], ["06", "Giugno"], ["07", "Luglio"], ["08", "Agosto"],
  ["09", "Settembre"], ["10", "Ottobre"], ["11", "Novembre"], ["12", "Dicembre"],
];
const lastDayOfMonth = (year, monthNum) => {
  const d = new Date(Number(year), Number(monthNum), 0).getDate();
  return String(d).padStart(2, "0");
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const PERIOD_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "today", label: "Oggi" },
  { value: "7d", label: "Ultimi 7 giorni" },
  { value: "30d", label: "Ultimi 30 giorni" },
  { value: "custom", label: "Personalizzato" },
];

const PAGE_SIZES = [10, 30, 50, 100];

// ─── Memoized option lists (Wave 2 rev2 perf, 30/06/2026) ──────────
// Stesso pattern adottato per il Diario: estraiamo le option list dei
// <select> in componenti memoizzati così le option JSX non vengono
// ricreate ad ogni keystroke nelle textarea Descrizione/NoteRapida/
// NoteChiusura, eliminando ~10-20ms di jitter per frame in dev.
// Toolbar e modale edit condividono gli stessi component memoizzati.
const MgmtSelectOptions = React.memo(function MgmtSelectOptions() {
  return (
    <>
      {MGMT_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </>
  );
});

const AreaSelectOptionsBody = React.memo(function AreaSelectOptionsBody() {
  // Le area options pure, senza option blank. Riutilizzata dai filtri
  // (con blank "Tutte" gestito dal caller) e dalla modale edit.
  return (
    <>
      {(AREA_OPTIONS || []).map((a) => {
        const v = typeof a === "string" ? a : a.value;
        const l = typeof a === "string" ? getAreaLabel(a) : a.label;
        return <option key={v} value={v}>{l}</option>;
      })}
    </>
  );
});

const AreaSelectOptionsWithBlank = React.memo(function AreaSelectOptionsWithBlank() {
  // Per modale edit (blank label "—").
  return (
    <>
      <option value="">—</option>
      <AreaSelectOptionsBody />
    </>
  );
});

const OperatorsSelectOptionsList = React.memo(function OperatorsSelectOptionsList({ operators }) {
  return (
    <>
      {operators.map((o) => (
        <option key={o.id} value={o.id}>{o.fullName || o.name || o.id}</option>
      ))}
    </>
  );
});

const PeriodSelectOptionsList = React.memo(function PeriodSelectOptionsList() {
  return (
    <>
      {PERIOD_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </>
  );
});

// ─── Modale inline (no Radix, no portal, no Presence) ─────────────────
// CRITICO 28/02/2026: la modale del Lavoro Quotidiano è un form di
// data-entry operativa. Il backdrop NON deve mai chiudere il dialog,
// per evitare perdita accidentale di dati (richiesta utente esplicita,
// ufficio reale).
// Default: `closeOnBackdrop=false`, `closeOnEsc=false`. Solo l'X in
// alto, "Annulla" o "Salva" possono dismettere il dialog.
function InlineModal({
  open,
  onClose,
  title,
  children,
  footer,
  wide = false,
  closeOnBackdrop = false,
  closeOnEsc = false,
}) {
  React.useEffect(() => {
    if (!open || !closeOnEsc) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, closeOnEsc, onClose]);

  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 50, padding: 16,
      }}
      onClick={closeOnBackdrop ? onClose : undefined}
      data-testid="inline-modal-backdrop"
    >
      <div
        style={{
          background: "white", borderRadius: 8, boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          width: "100%", maxWidth: wide ? 880 : 480, maxHeight: "90vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b", padding: 0, lineHeight: 1 }}
            aria-label="Chiudi"
            data-testid="inline-modal-close-x"
          >×</button>
        </div>
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = { padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, width: "100%", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 11, textTransform: "uppercase", color: "#64748b", marginBottom: 2 };
const btnPrimary = { padding: "6px 12px", background: "#0d9488", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 500 };
const btnSecondary = { padding: "6px 12px", background: "white", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer", fontSize: 13 };
const btnGhost = { background: "none", border: "none", color: "#475569", cursor: "pointer", padding: "2px 6px", fontSize: 13 };

// ─── Sub-componenti stabili (no Radix, no portal) ─────────────────
// Implementazioni pure DOM + React state per soddisfare i requisiti
// 28/02/2026 senza reintrodurre i pattern instabili che avevano
// causato i crash React 19. Tutti renderizzati nel tree React locale
// del Lavoro Quotidiano — niente <Portal>, niente <AnimatePresence>,
// niente DOM mutations esterne.

function ClientSearchAutocomplete({ value, clientId, clients, onSelect, onCreateRequest, "data-testid": testId }) {
  const [query, setQuery] = React.useState(value || "");
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setQuery(value || ""); }, [value]);

  const normalized = (query || "").trim().toLowerCase();
  const matches = !normalized
    ? clients.slice(0, 15)
    : clients.filter((c) => {
        const hay = `${c.fullName || ""} ${c.name || ""} ${c.companyName || ""} ${c.taxCode || ""} ${c.vatNumber || ""} ${c.email || ""}`.toLowerCase();
        return hay.includes(normalized);
      }).slice(0, 30);

  const handleSelect = (c) => {
    onSelect(c);
    setQuery(c.fullName || c.name || "");
    setOpen(false);
  };

  return (
    <div style={{ position: "relative" }} data-testid={testId}>
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Cerca per nome, P.IVA, codice fiscale..."
        style={{ padding: "6px 8px", border: clientId ? "1px solid #10b981" : "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, width: "100%", boxSizing: "border-box" }}
        data-testid="pq-client-autocomplete-input"
      />
      {open && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 60,
            background: "white", border: "1px solid #cbd5e1", borderRadius: 4,
            maxHeight: 240, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            marginTop: 2,
          }}
          data-testid="pq-client-autocomplete-list"
        >
          {matches.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 12, color: "#64748b" }}>
              <div style={{ marginBottom: 6 }}>Nessun cliente trovato per &ldquo;{query}&rdquo;.</div>
              {onCreateRequest && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onCreateRequest(); setOpen(false); }}
                  style={{ background: "#10b981", color: "white", border: "none", borderRadius: 4, padding: "6px 10px", cursor: "pointer", fontSize: 12, fontWeight: 500 }}
                  data-testid="pq-client-create-new-btn"
                >+ Crea nuovo cliente</button>
              )}
            </div>
          ) : (
            <>
              {matches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "6px 10px", border: "none", background: c.id === clientId ? "#ecfdf5" : "white",
                    cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f1f5f9",
                  }}
                  data-testid={`pq-client-option-${c.id}`}
                >
                  <div style={{ fontWeight: 500 }}>{c.fullName || c.name || c.companyName || "(senza nome)"}</div>
                  {(c.taxCode || c.vatNumber) && (
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      {c.taxCode ? `CF ${c.taxCode}` : ""}{c.taxCode && c.vatNumber ? " · " : ""}{c.vatNumber ? `P.IVA ${c.vatNumber}` : ""}
                    </div>
                  )}
                </button>
              ))}
              {onCreateRequest && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onCreateRequest(); setOpen(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: "none", background: "#f8fafc", cursor: "pointer", fontSize: 12, color: "#0d9488", fontWeight: 500 }}
                  data-testid="pq-client-create-new-btn"
                >+ Crea nuovo cliente</button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CoOperatorsPicker (Wave 2 perf, 28/06/2026) ─────────────────
// Lista chip multi-select dei co-operatori. Avvolta in React.memo
// per evitare ridisegni della lista (~100 chip) ad ogni keystroke
// nelle textarea della modal di Lavoro Quotidiano. Le prop arrivano
// stabilizzate dal parent (useMemo per `value`, useCallback per
// `onChange`). Internamente usiamo latest-ref per il toggle handler
// così la callback passata ai chip resta stabile e ogni chip
// memoizzato (CoOperatorChip) baila lo shallow compare.
const CoOperatorChip = React.memo(function CoOperatorChip({ operator, selected, onToggle }) {
  const handleClick = useCallback(() => onToggle(operator.id), [onToggle, operator.id]);
  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        padding: "4px 10px", borderRadius: 999, fontSize: 12, cursor: "pointer",
        border: selected ? "1px solid #0d9488" : "1px solid #cbd5e1",
        background: selected ? "#ccfbf1" : "white",
        color: selected ? "#0f766e" : "#475569",
      }}
      data-testid={`pq-co-operator-${operator.id}`}
    >
      {selected ? "✓ " : ""}{operator.fullName || operator.name || operator.id}
    </button>
  );
});

const CoOperatorsPicker = React.memo(function CoOperatorsPicker({ value = [], operators = [], excludeId, onChange }) {
  // Latest-ref pattern: il toggle handler resta stabile tra render
  // (deps vuote), così i chip non vedono nuove reference e baileano
  // lo shallow compare di React.memo. Aggiorniamo i ref ad ogni
  // render senza creare nuovi handler.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  const valueSet = useMemo(() => new Set(value), [value]);
  const list = useMemo(
    () => operators.filter((o) => o.id && o.id !== excludeId),
    [operators, excludeId]
  );

  const toggle = useCallback((id) => {
    const next = new Set(valueRef.current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChangeRef.current(Array.from(next));
  }, []);

  if (list.length === 0) {
    return <div style={{ fontSize: 12, color: "#94a3b8" }}>Nessun altro operatore disponibile</div>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="pq-co-operators-picker">
      {list.map((o) => (
        <CoOperatorChip
          key={o.id}
          operator={o}
          selected={valueSet.has(o.id)}
          onToggle={toggle}
        />
      ))}
    </div>
  );
});

function ExpensesRowsEditor({ rows = [], onChange }) {
  const update = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeRow = (idx) => {
    const next = rows.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const addRow = () => onChange([...rows, { amount: 0, description: "", paid: false, paymentMethod: "" }]);
  return (
    <div data-testid="pq-expenses-editor">
      {rows.length === 0 && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>Nessuna voce. Aggiungi righe per bolli, F24, raccomandate, diritti, visure, contributi…</div>
      )}
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "100px 1fr 130px 24px", gap: 6, marginBottom: 6, alignItems: "center" }}>
          <input
            type="number" min="0" step="0.01"
            value={r.amount || ""}
            onChange={(e) => update(i, { amount: parseFloat(e.target.value) || 0 })}
            placeholder="€"
            style={{ padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, width: "100%", boxSizing: "border-box" }}
            data-testid={`pq-expense-amount-${i}`}
          />
          <input
            type="text"
            value={r.description || ""}
            onChange={(e) => update(i, { description: e.target.value })}
            placeholder="Es. marca da bollo, F24, raccomandata"
            style={{ padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, width: "100%", boxSizing: "border-box" }}
            data-testid={`pq-expense-desc-${i}`}
          />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={!!r.paid}
              onChange={(e) => update(i, { paid: e.target.checked })}
              data-testid={`pq-expense-paid-${i}`}
            />
            Già pagato
          </label>
          <button
            type="button"
            onClick={() => removeRow(i)}
            style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", padding: 0, fontSize: 16 }}
            aria-label="Rimuovi voce"
            title="Rimuovi voce"
            data-testid={`pq-expense-remove-${i}`}
          >×</button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        style={{ padding: "5px 10px", background: "white", border: "1px dashed #94a3b8", borderRadius: 4, color: "#475569", cursor: "pointer", fontSize: 12 }}
        data-testid="pq-expense-add-btn"
      >+ Aggiungi voce spesa</button>
    </div>
  );
}

function OperationalNotesSection({ practiceId, notes = [], onAdded }) {
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const updated = await api.addDailyPracticeOperationalNote(practiceId, t);
      onAdded(updated);
      setText("");
    } catch (e) {
      try { toast.error(e?.message || "Errore aggiunta nota"); } catch { /* ignore */ }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="pq-op-notes-section">
      {(notes || []).length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 8px", maxHeight: 160, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 4 }}>
          {notes.map((n, i) => (
            <li key={`${n.timestamp || ""}-${i}`} style={{ padding: "6px 8px", borderBottom: "1px dotted #e2e8f0", fontSize: 12 }}>
              <div style={{ color: "#94a3b8", fontSize: 11 }}>
                {(n.timestamp || "").slice(0, 16).replace("T", " ")} · {n.authorName || "operatore"}
              </div>
              <div style={{ color: "#1e293b", whiteSpace: "pre-wrap" }}>{n.text}</div>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Aggiungi dettaglio operativo (es. inviata raccomandata, contattato cliente)..."
          style={{ flex: 1, padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13 }}
          data-testid="pq-op-note-input"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <button
          type="button"
          onClick={add}
          disabled={busy || !text.trim()}
          style={{ padding: "6px 12px", background: "#0d9488", color: "white", border: "none", borderRadius: 4, cursor: busy ? "not-allowed" : "pointer", fontSize: 12, opacity: busy ? 0.6 : 1 }}
          data-testid="pq-op-note-add-btn"
        >Aggiungi</button>
      </div>
    </div>
  );
}



const todayHMNow = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// ─── WorkRow (Wave 2 perf, 28/06/2026) ─────────────────────────────
// Riga singola della tabella Lavoro Quotidiano. Avvolta in React.memo
// così, quando il parent re-renderizza per cambi a `editingForm`,
// `noteText`, `closeNotes` (digitazione nelle 3 textarea della modal),
// ogni riga fa un shallow compare delle prop e baila se nulla è
// cambiato. Gli handler in parent sono già stabili (useCallback).
// Impatto: con 50 righe paginate, il costo del re-render durante la
// digitazione passa da ~50 reconciliation complete a 50 shallow
// equality check (trascurabile).
const WorkRow = React.memo(function WorkRow({ p, expanded, onToggleExpand, onNote, onClose, onPrint, onEdit }) {
  return (
    <tbody>
      <tr style={{ borderBottom: "1px solid #e2e8f0", background: expanded ? "#f8fafc" : "transparent" }}>
        <td style={{ padding: "6px 8px" }}>
          <button
            type="button"
            onClick={() => onToggleExpand(p.id)}
            style={{ ...btnGhost, padding: 0, fontWeight: 500, color: "#0f172a", textAlign: "left" }}
            title="Mostra cronologia"
          >
            {p.clientName || "—"}
          </button>
        </td>
        <td style={{ padding: "6px 8px" }}>
          {p.managementType === "studio" ? "Studio" : "Agenzia"}
          {p.operationalArea && <span style={{ color: "#94a3b8", marginLeft: 4 }}>· {getAreaLabel(p.operationalArea)}</span>}
        </td>
        <td style={{ padding: "6px 8px" }}>{p.creatorOperatorName || "—"}</td>
        <td style={{ padding: "6px 8px" }}>{p.expectedDueDate || "—"}</td>
        <td style={{ padding: "6px 8px" }}>
          {p.status === "chiusa"
            ? <span style={{ padding: "2px 6px", background: "#dcfce7", color: "#166534", borderRadius: 4, fontSize: 11 }}>Chiusa</span>
            : <span style={{ padding: "2px 6px", background: "#dbeafe", color: "#1e40af", borderRadius: 4, fontSize: 11 }}>In corso</span>}
        </td>
        <td style={{ padding: "6px 8px", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.description || "—"}</td>
        <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
          <button type="button" onClick={() => onNote(p)} style={btnGhost} data-testid={`pq-action-note-${p.id}`}>
            <span aria-hidden="true">✎</span> <span style={{ marginLeft: 4 }}>Nota</span>
          </button>
          {p.status !== "chiusa" && (
            <button type="button" onClick={() => onClose(p)} style={{ ...btnGhost, color: "#047857" }} data-testid={`pq-action-close-${p.id}`}>
              <span aria-hidden="true">✓</span> <span style={{ marginLeft: 4 }}>Chiudi</span>
            </button>
          )}
          <button type="button" onClick={() => onPrint(p)} style={btnGhost} data-testid={`pq-action-print-${p.id}`}>
            <span aria-hidden="true">🖨</span> <span style={{ marginLeft: 4 }}>Stampa</span>
          </button>
          <button type="button" onClick={() => onEdit(p)} style={btnGhost} data-testid={`pq-action-edit-${p.id}`}>
            <span aria-hidden="true">✏</span> <span style={{ marginLeft: 4 }}>Modifica</span>
          </button>
        </td>
      </tr>
      <tr hidden={!expanded} data-testid={`pq-history-row-${p.id}`}>
        <td colSpan={7} style={{ background: "#f8fafc", padding: 12, fontSize: 12, color: "#475569" }}>
          {expanded && (
            <div>
              <strong>Cronologia</strong>
              {Array.isArray(p.history) && p.history.length > 0 ? (
                <ul style={{ listStyle: "none", padding: 0, margin: "6px 0" }}>
                  {p.history.map((h, i) => (
                    <li key={`${h.timestamp || ""}-${i}`} style={{ padding: "3px 0", borderBottom: "1px dotted #cbd5e1" }}>
                      <span style={{ color: "#94a3b8" }}>{(h.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                      {" — "}
                      <span style={{ color: "#475569" }}>{h.action || ""}</span>
                      {h.note && <span style={{ marginLeft: 6 }}>· {h.note}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: "#94a3b8", margin: "4px 0 0" }}>Nessun evento.</p>
              )}
            </div>
          )}
        </td>
      </tr>
    </tbody>
  );
});


const emptyForm = (currentOperator) => ({
  clientId: "",
  clientName: "",
  managementType: "agenzia",
  operationalArea: "",
  // Operatore autore: SEMPRE l'operatore loggato. Locked per audit.
  creatorOperatorId: currentOperator?.id || "",
  creatorOperatorName: currentOperator?.fullName || currentOperator?.name || "",
  // Data + Ora separati (28/02/2026). Default = adesso. Editabili.
  expectedDueDate: todayISO(),
  expectedDueTime: todayHMNow(),
  // Co-operatori opzionali (collaborative work visibility).
  coOperatorIds: [],
  description: "",
  // Bolli/Spese — multi-row. Vuoto di default.
  expensesList: [],
});

export default function PraticheQuotidianeTabRebuilt({
  clients = [],
  operators = [],
  currentOperator,
  operatorRole, // eslint-disable-line no-unused-vars
  // 28/02/2026 — Quando l'autocomplete cliente non trova match,
  // l'utente può cliccare "Crea nuovo cliente" → questa callback
  // (iniettata da App.js) apre il dialog cliente integrato.
  // Accetta callback opzionale `onCreated(newClient)` che la pagina
  // chiama dopo la creazione, così possiamo popolare il campo.
  onOpenNewClient = null,
  // 11/06/2026 — Quick-action flag from App.js. Click on "Lavoro
  // Quotidiano" in the top action bar sets pendingQuickAction ===
  // "lavoro:new"; this tab opens the creation dialog when mounted and
  // clears the flag via consumeQuickAction (one-shot).
  pendingQuickAction = null,
  consumeQuickAction = () => {},
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterYear, setFilterYear] = useState(() => String(new Date().getFullYear()));
  const [filterMonthFrom, setFilterMonthFrom] = useState("all");
  const [filterMonthTo, setFilterMonthTo] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterCustomFrom, setFilterCustomFrom] = useState("");
  const [filterCustomTo, setFilterCustomTo] = useState("");
  const [filterOperator, setFilterOperator] = useState("");
  const [filterMgmt, setFilterMgmt] = useState("");
  const [filterArea, setFilterArea] = useState("");
  // Default filter "Stato" = "open" (In corso) — richiesta utente
  // 28/02/2026. L'utente può comunque cambiare il filtro manualmente.
  const [filterStatus, setFilterStatus] = useState("open"); // all | open | closed

  // Sort
  const [sortKey, setSortKey] = useState("expectedDueDate");
  const [sortDir, setSortDir] = useState("desc");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("lq_rebuilt_pagesize"), 10);
      return Number.isFinite(v) && PAGE_SIZES.includes(v) ? v : 50;
    } catch { return 50; }
  });
  const setPageSizePersist = useCallback((n) => {
    setPageSize(n);
    try { localStorage.setItem("lq_rebuilt_pagesize", String(n)); } catch { /* ignore */ }
    setPage(1);
  }, []);

  // Expanded row (cronologia)
  const [expandedId, setExpandedId] = useState(null);

  // Modali
  const [editingItem, setEditingItem] = useState(null);
  const [editingForm, setEditingForm] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [closeTarget, setCloseTarget] = useState(null);
  const [closeDate, setCloseDate] = useState("");
  const [closeNotes, setCloseNotes] = useState("");

  // Load
  const loadItems = useCallback(() => {
    setLoading(true);
    setError(null);
    api.listDailyPractices()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.items || []);
        setItems(list);
        setLoading(false);
      })
      .catch((e) => {
        setError(e?.message || "Errore caricamento");
        setLoading(false);
      });
  }, []);
  useEffect(() => { loadItems(); }, [loadItems]);

  // Year options (data-driven)
  const yearOptions = useMemo(() => {
    const s = new Set();
    s.add(String(new Date().getFullYear()));
    items.forEach((p) => {
      const d = p.expectedDueDate;
      if (d && /^\d{4}-/.test(d)) s.add(d.slice(0, 4));
    });
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [items]);

  // Compute month range
  const monthRange = useMemo(() => {
    if (filterYear === "all") return null;
    const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
    const mToRaw = filterMonthTo !== "all" ? filterMonthTo : null;
    let m1 = mFrom, m2 = mToRaw || mFrom;
    if (m1 && m2 && m1 > m2) { const t = m1; m1 = m2; m2 = t; }
    const from = m1 ? `${filterYear}-${m1}-01` : `${filterYear}-01-01`;
    const to = m2 ? `${filterYear}-${m2}-${lastDayOfMonth(filterYear, m2)}` : `${filterYear}-12-31`;
    return { from, to };
  }, [filterYear, filterMonthFrom, filterMonthTo]);

  // Period range (preset)
  const periodRange = useMemo(() => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    switch (filterPeriod) {
      case "today": return { from: fmt(today), to: fmt(today) };
      case "7d": {
        const d = new Date(today); d.setDate(d.getDate() - 7);
        return { from: fmt(d), to: fmt(today) };
      }
      case "30d": {
        const d = new Date(today); d.setDate(d.getDate() - 30);
        return { from: fmt(d), to: fmt(today) };
      }
      case "custom": return { from: filterCustomFrom || "", to: filterCustomTo || "" };
      default: return null;
    }
  }, [filterPeriod, filterCustomFrom, filterCustomTo]);

  // Filter
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((p) => {
      if (filterStatus === "open" && p.status === "chiusa") return false;
      if (filterStatus === "closed" && p.status !== "chiusa") return false;
      if (filterOperator && p.creatorOperatorId !== filterOperator) return false;
      if (filterMgmt && p.managementType !== filterMgmt) return false;
      if (filterArea && p.operationalArea !== filterArea) return false;
      const d = (p.expectedDueDate || "").slice(0, 10);
      if (monthRange) {
        if (!d || d < monthRange.from || d > monthRange.to) return false;
      }
      if (periodRange) {
        if (periodRange.from && (!d || d < periodRange.from)) return false;
        if (periodRange.to && (!d || d > periodRange.to)) return false;
      }
      if (q) {
        const hay = `${p.clientName || ""} ${p.description || ""} ${p.operationalArea || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, searchQuery, filterStatus, filterOperator, filterMgmt, filterArea, monthRange, periodRange]);

  // Sort
  const sorted = useMemo(() => {
    const cp = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    cp.sort((a, b) => {
      const av = (a[sortKey] || "");
      const bv = (b[sortKey] || "");
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return cp;
  }, [filtered, sortKey, sortDir]);
  const toggleSort = useCallback((key) => {
    setSortKey((cur) => {
      if (cur === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return cur;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  // Paginate
  useEffect(() => { setPage(1); }, [searchQuery, filterStatus, filterOperator, filterMgmt, filterArea, filterYear, filterMonthFrom, filterMonthTo, filterPeriod]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize]
  );

  // Counts
  const openCount = useMemo(() => items.filter((p) => p.status !== "chiusa").length, [items]);
  const closedCount = items.length - openCount;

  // Reset
  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setFilterYear(String(new Date().getFullYear()));
    setFilterMonthFrom("all");
    setFilterMonthTo("all");
    setFilterPeriod("all");
    setFilterCustomFrom("");
    setFilterCustomTo("");
    setFilterOperator("");
    setFilterMgmt("");
    setFilterArea("");
    setFilterStatus("all");
  }, []);
  const hasActiveFilters = Boolean(
    searchQuery.trim() || filterPeriod !== "all" || filterOperator ||
    filterMgmt || filterArea || filterStatus !== "all" ||
    filterYear !== "all" || filterMonthFrom !== "all" || filterMonthTo !== "all"
  );

  // Toast post-success (uscire dal batch React per evitare race)
  const toastAfter = useCallback((kind, msg) => {
    setTimeout(() => {
      try { (kind === "error" ? toast.error : toast.success)(msg); } catch { /* ignore */ }
    }, 0);
  }, []);

  // Open dialogs
  const openCreate = useCallback(() => {
    setEditingItem({ id: null });
    setEditingForm(emptyForm(currentOperator));
  }, [currentOperator]);

  // 11/06/2026 — Prop-based quick-action consumer. When the top
  // "Lavoro Quotidiano" button in App.js fires (sets pendingQuickAction
  // === "lavoro:new"), we open the creation dialog immediately upon
  // mount and clear the flag, so the user lands in the new-work form
  // with one click — parity with Nuova Pratica / Nuova Fattura.
  useEffect(() => {
    if (pendingQuickAction === "lavoro:new") {
      openCreate();
      consumeQuickAction("lavoro:new");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuickAction]);
  const openEdit = useCallback((p) => {
    setEditingItem(p);
    setEditingForm({
      clientId: p.clientId || "",
      clientName: p.clientName || "",
      managementType: p.managementType || "agenzia",
      operationalArea: p.operationalArea || "",
      // Operatore primario: mai sovrascritto in edit. Mostriamo
      // l'autore originale (read-only) per audit clarity.
      creatorOperatorId: p.creatorOperatorId || currentOperator?.id || "",
      creatorOperatorName: p.creatorOperatorName || currentOperator?.fullName || currentOperator?.name || "",
      expectedDueDate: (p.expectedDueDate || todayISO()).slice(0, 10),
      expectedDueTime: p.expectedDueTime || todayHMNow(),
      coOperatorIds: Array.isArray(p.coOperatorIds) ? p.coOperatorIds : [],
      description: p.description || "",
      expensesList: Array.isArray(p.expensesList)
        ? p.expensesList
        : (p.expenses && p.expenses.amount ? [p.expenses] : []),
    });
  }, [currentOperator]);
  const closeEditModal = useCallback(() => { setEditingItem(null); setEditingForm(null); }, []);

  const saveEdit = useCallback(async () => {
    if (!editingForm) return;
    if (!editingForm.clientId) {
      toastAfter("error", "Seleziona un cliente");
      return;
    }
    try {
      const coOperatorNames = (editingForm.coOperatorIds || [])
        .map((id) => {
          const op = operators.find((o) => o.id === id);
          return op?.fullName || op?.name || id;
        });
      const payload = {
        clientId: editingForm.clientId,
        clientName: editingForm.clientName,
        managementType: editingForm.managementType,
        operationalArea: editingForm.operationalArea || null,
        creatorOperatorId: editingForm.creatorOperatorId || currentOperator?.id || null,
        creatorOperatorName: editingForm.creatorOperatorName || currentOperator?.fullName || "",
        expectedDueDate: editingForm.expectedDueDate || todayISO(),
        expectedDueTime: editingForm.expectedDueTime || "",
        description: editingForm.description || "",
        coOperatorIds: editingForm.coOperatorIds || [],
        coOperatorNames,
        expensesList: (editingForm.expensesList || []).filter(
          (r) => (Number(r.amount) > 0) || (r.description || "").trim()
        ),
        // Nuovo workflow (26/02/2026): le pratiche nascono APERTE per
        // default. La chiusura è un'azione esplicita amministrativa
        // (revisione/fatturazione), non operativa.
        isOpen: true,
      };
      let saved;
      if (editingItem?.id) {
        saved = await api.updateDailyPractice(editingItem.id, payload);
        setItems((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
        toastAfter("success", "Lavoro aggiornato");
      } else {
        saved = await api.createDailyPractice(payload);
        setItems((prev) => [saved, ...prev]);
        toastAfter("success", "Lavoro creato");
      }
      closeEditModal();
    } catch (e) {
      toastAfter("error", e?.message || "Errore salvataggio");
    }
  }, [editingForm, editingItem, closeEditModal, toastAfter, currentOperator, operators]);

  // Note
  const openNote = useCallback((p) => { setNoteTarget(p); setNoteText(""); }, []);
  const closeNoteModal = useCallback(() => { setNoteTarget(null); setNoteText(""); }, []);
  const saveNote = useCallback(async () => {
    if (!noteTarget || !noteText.trim()) {
      toastAfter("error", "Inserisci una nota");
      return;
    }
    try {
      const updated = await api.addDailyPracticeNote(noteTarget.id, noteText.trim());
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      closeNoteModal();
      toastAfter("success", "Nota aggiunta");
    } catch (e) {
      toastAfter("error", e?.message || "Errore aggiunta nota");
    }
  }, [noteTarget, noteText, closeNoteModal, toastAfter]);

  // Close
  const openCloseFlow = useCallback((p) => {
    setCloseTarget(p);
    setCloseDate(todayISO());
    setCloseNotes("");
  }, []);
  const closeCloseModal = useCallback(() => { setCloseTarget(null); setCloseDate(""); setCloseNotes(""); }, []);
  const confirmClose = useCallback(async () => {
    if (!closeTarget) return;
    try {
      const closedAt = new Date(`${closeDate || todayISO()}T${new Date().toTimeString().slice(0, 5)}`).toISOString();
      const updated = await api.updateDailyPractice(closeTarget.id, {
        ...closeTarget,
        status: "chiusa",
        closedAt,
        closureNotes: closeNotes || "",
      });
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      closeCloseModal();
      toastAfter("success", "Lavoro chiuso");
    } catch (e) {
      toastAfter("error", e?.message || "Errore chiusura");
    }
  }, [closeTarget, closeDate, closeNotes, closeCloseModal, toastAfter]);

  // Wave 2 perf — handler stabile per il toggle riga, così <WorkRow>
  // memoizzato non riceve callback inline che cambierebbero ad ogni render.
  const handleToggleExpand = useCallback((id) => {
    setExpandedId((cur) => (cur === id ? null : id));
  }, []);

  // Wave 2 perf (28/06/2026) — handler stabile per CoOperatorsPicker
  // così la lista di ~100 chip operatori non viene re-renderizzata
  // ad ogni keystroke nelle textarea della modal di modifica.
  const handleEditCoOpChange = useCallback((ids) => {
    setEditingForm((f) => ({ ...f, coOperatorIds: ids }));
  }, []);

  // Print single — inline HTML in new window (no API, no DOM mut).
  // 28/02/2026 — Estesa per includere TUTTE le sezioni operative:
  // dati principali, co-operatori, descrizione, bolli/spese, note
  // operative aggiuntive, cronologia/audit (history) e timestamp di
  // creazione/aggiornamento.
  const handlePrintSingle = useCallback((p) => {
    if (!p) return;
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) { toastAfter("error", "Apertura finestra di stampa bloccata dal browser"); return; }
    const esc = (s) => String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[ch]));
    const dueDate = p.expectedDueDate ? new Date(p.expectedDueDate + "T00:00:00").toLocaleDateString("it-IT") : "—";
    const dueTime = p.expectedDueTime || "";
    const todayStr = new Date().toLocaleString("it-IT");
    const createdStr = p.createdAt ? new Date(p.createdAt).toLocaleString("it-IT") : "—";
    const updatedStr = p.updatedAt ? new Date(p.updatedAt).toLocaleString("it-IT") : "—";
    const coOps = (p.coOperatorNames && p.coOperatorNames.length)
      ? p.coOperatorNames.join(", ")
      : "—";
    const expenses = Array.isArray(p.expensesList) && p.expensesList.length
      ? p.expensesList
      : (p.expenses && (p.expenses.amount || p.expenses.description) ? [p.expenses] : []);
    const expensesRows = expenses.map((r) =>
      `<tr><td>${esc(r.description || "—")}</td><td style="text-align:right">${(Number(r.amount) || 0).toFixed(2).replace(".", ",")} €</td><td>${r.paid ? "✓ Pagata" : "—"}</td></tr>`
    ).join("");
    const opNotesItems = (p.operationalNotes || []).map((n) =>
      `<li><div style="color:#64748b;font-size:11px">${esc((n.timestamp || "").slice(0, 16).replace("T", " "))} · ${esc(n.authorName || "operatore")}</div><div>${esc(n.text || "")}</div></li>`
    ).join("");
    const historyItems = (p.history || []).map((h) => {
      const label = h.field
        ? `${esc(h.field)}: ${esc(h.oldValue || "—")} → ${esc(h.newValue || "—")}`
        : `${esc(h.action || "—")}${h.details ? ` — ${esc(h.details)}` : ""}`;
      return `<li><span style="color:#94a3b8">${esc((h.timestamp || "").slice(0, 16).replace("T", " "))}</span> · <strong>${esc(h.operatorName || h.userId || "—")}</strong> · ${label}</li>`;
    }).join("");

    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/>
      <title>Scheda lavoro — ${esc(p.clientName || "")}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;max-width:800px;margin:0 auto;color:#1e293b}h1{margin:0 0 4px}.meta{color:#64748b;font-size:11px;margin-bottom:14px}table{width:100%;border-collapse:collapse;margin-top:6px}td,th{padding:6px 10px;border-bottom:1px solid #e2e8f0;font-size:12px;vertical-align:top;text-align:left}th{background:#f1f5f9;font-weight:600;color:#475569}.label{background:#f8fafc;width:35%;color:#475569;font-weight:600}.section{margin:16px 0 4px;font-size:13px;font-weight:700;border-bottom:2px solid #cbd5e1;padding-bottom:3px;color:#0f172a}.longtext{white-space:pre-wrap;background:#fafafa;border:1px solid #e2e8f0;padding:10px;border-radius:4px;font-size:12px;line-height:1.5}ul{margin:6px 0;padding-left:18px;font-size:12px}ul li{margin-bottom:4px}@media print{button{display:none}}</style>
      </head><body>
      <h1>Scheda Lavoro Quotidiano</h1>
      <div class="meta">Stampato il ${esc(todayStr)} · ID ${esc(p.id || "—")} · ${p.status === "chiusa" ? "Chiusa (revisionata)" : "In corso"}</div>
      <div class="section">Dati principali</div>
      <table>
        <tr><td class="label">Cliente</td><td>${esc(p.clientName || "—")}</td></tr>
        <tr><td class="label">Gestione</td><td>${esc(p.managementType === "studio" ? "Studio" : "Agenzia")}</td></tr>
        <tr><td class="label">Area operativa</td><td>${esc(p.operationalArea || "—")}</td></tr>
        <tr><td class="label">Operatore (autore)</td><td>${esc(p.creatorOperatorName || "—")}</td></tr>
        <tr><td class="label">Co-operatori</td><td>${esc(coOps)}</td></tr>
        <tr><td class="label">Data attività</td><td>${esc(dueDate)}${dueTime ? " — ore " + esc(dueTime) : ""}</td></tr>
        <tr><td class="label">Creato il</td><td>${esc(createdStr)}</td></tr>
        <tr><td class="label">Ultima modifica</td><td>${esc(updatedStr)}</td></tr>
      </table>
      <div class="section">Descrizione attività</div>
      <div class="longtext">${esc(p.description || "(nessuna descrizione)")}</div>
      ${expenses.length > 0 ? `<div class="section">Bolli / Spese</div>
        <table><tr><th>Descrizione</th><th style="text-align:right">Importo</th><th>Stato</th></tr>${expensesRows}</table>` : ""}
      ${opNotesItems ? `<div class="section">Note aggiuntive operative</div><ul>${opNotesItems}</ul>` : ""}
      ${historyItems ? `<div class="section">Cronologia modifiche (audit)</div><ul>${historyItems}</ul>` : ""}
      ${p.status === "chiusa" ? `<div class="section">Revisione amministrativa</div><table>${p.closedAt ? `<tr><td class="label">Revisionata il</td><td>${esc(new Date(p.closedAt).toLocaleString("it-IT"))}</td></tr>` : ""}${p.closureNotes ? `<tr><td class="label">Note di revisione</td><td>${esc(p.closureNotes)}</td></tr>` : ""}</table>` : ""}
      <script>window.onload=()=>{setTimeout(()=>window.print(),200);}</script>
      </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  }, [toastAfter]);

  const sortIndicator = (key) => sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div data-testid="lavoro-quotidiano-tab">
      {/* Stili responsive locali per la modale "Nuovo/Modifica lavoro".
          Desktop ≥1024px: 3 colonne. Tablet 640-1023px: 2 colonne.
          Mobile <640px: 1 colonna. */}
      <style>{`
        .pq-edit-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        @media (max-width: 1023px) {
          .pq-edit-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 639px) {
          .pq-edit-grid { grid-template-columns: 1fr; }
        }
        .pq-edit-grid > .pq-col-full { grid-column: 1 / -1; }
      `}</style>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 220px", minWidth: 200 }}>
          <label style={labelStyle}>Cerca</label>
          <input
            type="text"
            placeholder="Cliente / descrizione / area"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={inputStyle}
            data-testid="pq-search"
          />
        </div>
        <div>
          <label style={labelStyle}>Stato</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={inputStyle} data-testid="pq-status">
            <option value="all">Tutti</option>
            <option value="open">In corso</option>
            <option value="closed">Chiuse</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Gestione</label>
          <select value={filterMgmt} onChange={(e) => setFilterMgmt(e.target.value)} style={inputStyle}>
            <option value="">Tutte</option>
            <MgmtSelectOptions />
          </select>
        </div>
        <div>
          <label style={labelStyle}>Area</label>
          <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)} style={inputStyle}>
            <option value="">Tutte</option>
            <AreaSelectOptionsBody />
          </select>
        </div>
        <div>
          <label style={labelStyle}>Operatore</label>
          <select value={filterOperator} onChange={(e) => setFilterOperator(e.target.value)} style={inputStyle}>
            <option value="">Tutti</option>
            <OperatorsSelectOptionsList operators={operators} />
          </select>
        </div>
        <div>
          <label style={labelStyle}>Periodo</label>
          <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} style={inputStyle}>
            <PeriodSelectOptionsList />
          </select>
        </div>
        {filterPeriod === "custom" && (
          <>
            <div><label style={labelStyle}>Dal</label><input type="date" value={filterCustomFrom} onChange={(e) => setFilterCustomFrom(e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>Al</label><input type="date" value={filterCustomTo} onChange={(e) => setFilterCustomTo(e.target.value)} style={inputStyle} /></div>
          </>
        )}
        <div>
          <label style={labelStyle}>Anno</label>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} style={inputStyle} data-testid="pq-year-filter">
            <option value="all">Tutti</option>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Mese da</label>
          <select value={filterMonthFrom} onChange={(e) => setFilterMonthFrom(e.target.value)} disabled={filterYear === "all"} style={inputStyle} data-testid="pq-month-from-filter">
            <option value="all">Tutti i mesi</option>
            {MONTHS_IT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Mese a</label>
          <select value={filterMonthTo} onChange={(e) => setFilterMonthTo(e.target.value)} disabled={filterYear === "all" || filterMonthFrom === "all"} style={inputStyle} data-testid="pq-month-to-filter">
            <option value="all">—</option>
            {MONTHS_IT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {hasActiveFilters && (
            <button type="button" onClick={resetFilters} style={btnSecondary} data-testid="pq-reset-filters">
              Reset filtri
            </button>
          )}
          <button type="button" onClick={openCreate} style={btnPrimary} data-testid="pq-new-btn">
            + Nuovo lavoro
          </button>
        </div>
      </div>

      {/* Header info */}
      <div style={{ fontSize: 13, color: "#475569", marginBottom: 8 }} data-testid="pq-active-section">
        {loading ? "Caricamento…" : `${sorted.length} lavori (${openCount} in corso · ${closedCount} chiusi · ${items.length} totali)`}
        {error && <span style={{ color: "#dc2626", marginLeft: 8 }}>· Errore: {error}</span>}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
              <th style={{ padding: "8px", cursor: "pointer", borderBottom: "1px solid #cbd5e1" }} onClick={() => toggleSort("clientName")}>Cliente{sortIndicator("clientName")}</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Gestione / Area</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Operatore</th>
              <th style={{ padding: "8px", cursor: "pointer", borderBottom: "1px solid #cbd5e1" }} onClick={() => toggleSort("expectedDueDate")}>Data lavoro{sortIndicator("expectedDueDate")}</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Stato</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Descrizione</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1", textAlign: "right" }}>Azioni</th>
            </tr>
          </thead>
          {/* Empty-state SEMPRE renderizzato in proprio tbody con hidden */}
          <tbody hidden={paged.length !== 0}>
            <tr>
              <td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
                {loading ? "Caricamento…" : "Nessun lavoro corrisponde ai filtri."}
              </td>
            </tr>
          </tbody>
          {/* Una riga = un tbody con key db stabile (multi-tbody pattern HTML5
              ammesso, evita conditional row count nel singolo tbody).
              Wave 2 perf (28/06/2026): <WorkRow> è memoizzato — typing
              nei modal del parent NON ri-renderizza le 50 righe paginate. */}
          {paged.map((p) => (
            <WorkRow
              key={p.id}
              p={p}
              expanded={expandedId === p.id}
              onToggleExpand={handleToggleExpand}
              onNote={openNote}
              onClose={openCloseFlow}
              onPrint={handlePrintSingle}
              onEdit={openEdit}
            />
          ))}
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", fontSize: 12, color: "#475569", flexWrap: "wrap" }}>
        <span>Pagina</span>
        <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={btnSecondary} data-testid="pq-page-prev">«</button>
        <select value={page} onChange={(e) => setPage(parseInt(e.target.value, 10) || 1)} style={{ ...inputStyle, width: 70 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>di {totalPages}</span>
        <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={btnSecondary} data-testid="pq-page-next">»</button>
        <span style={{ marginLeft: 12 }}>righe</span>
        <select value={pageSize} onChange={(e) => setPageSizePersist(parseInt(e.target.value, 10))} style={{ ...inputStyle, width: 70 }}>
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span style={{ marginLeft: "auto" }}>
          {sorted.length === 0 ? "0 risultati" : `${(page - 1) * pageSize + 1} – ${Math.min(sorted.length, page * pageSize)} di ${sorted.length}`}
        </span>
      </div>

      {/* ─── Modali inline (no Radix, no portal) ─── */}
      <InlineModal
        open={!!editingItem}
        onClose={closeEditModal}
        wide
        title={editingItem?.id ? "Modifica lavoro" : "Nuovo lavoro"}
        footer={
          <>
            {/* "Stampa scheda" prima di Annulla/Salva (richiesta utente).
                Disabilitato in modalità "Nuovo" prima che il record
                esista realmente — la stampa rende uno snapshot del record
                già salvato. */}
            <button
              type="button"
              onClick={() => editingItem?.id && handlePrintSingle(editingItem)}
              style={{ ...btnSecondary, opacity: editingItem?.id ? 1 : 0.5, cursor: editingItem?.id ? "pointer" : "not-allowed" }}
              disabled={!editingItem?.id}
              data-testid="pq-print-record-btn"
              title={editingItem?.id ? "Stampa scheda completa" : "Disponibile dopo il primo salvataggio"}
            >
              🖶 Stampa scheda
            </button>
            <button type="button" onClick={closeEditModal} style={btnSecondary} data-testid="pq-cancel-btn">Annulla</button>
            <button type="button" onClick={saveEdit} style={btnPrimary} data-testid="pq-save-btn">Salva</button>
          </>
        }
      >
        {editingForm && (
          <div className="pq-edit-grid">
            {/* Cliente — autocomplete searchable (28/02/2026) */}
            <div className="pq-col-full">
              <label style={labelStyle}>
                Cliente <span style={{ color: "#b91c1c" }}>*</span>
              </label>
              <ClientSearchAutocomplete
                value={editingForm.clientName || ""}
                clientId={editingForm.clientId}
                clients={clients}
                onSelect={(c) =>
                  setEditingForm((f) => ({
                    ...f,
                    clientId: c.id,
                    clientName: c.fullName || c.name || "",
                  }))
                }
                onCreateRequest={onOpenNewClient ? (() => onOpenNewClient((newClient) => {
                  // Callback popola automaticamente il campo dopo
                  // la creazione lato ClientDialog.
                  setEditingForm((f) => ({
                    ...f,
                    clientId: newClient?.id || "",
                    clientName: newClient?.fullName || newClient?.name || "",
                  }));
                })) : null}
                data-testid="pq-modal-client"
              />
            </div>

            {/* Operatore — autolocked (28/02/2026) */}
            <div>
              <label style={labelStyle}>Operatore (autore)</label>
              <input
                type="text"
                value={editingForm.creatorOperatorName || "—"}
                readOnly
                style={{ ...inputStyle, background: "#f1f5f9", color: "#475569", cursor: "not-allowed" }}
                data-testid="pq-modal-operator-locked"
                title="Operatore loggato — non modificabile (audit)"
              />
            </div>

            {/* Data + Ora separati (28/02/2026) */}
            <div>
              <label style={labelStyle}>Data *</label>
              <input
                type="date"
                value={editingForm.expectedDueDate || ""}
                onChange={(e) => setEditingForm((f) => ({ ...f, expectedDueDate: e.target.value }))}
                style={inputStyle}
                data-testid="pq-modal-date"
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Ora *</label>
              <input
                type="time"
                value={editingForm.expectedDueTime || ""}
                onChange={(e) => setEditingForm((f) => ({ ...f, expectedDueTime: e.target.value }))}
                style={inputStyle}
                data-testid="pq-modal-time"
                required
              />
            </div>

            {/* Gestione + Area operativa */}
            <div>
              <label style={labelStyle}>Gestione</label>
              <select
                value={editingForm.managementType}
                onChange={(e) => setEditingForm((f) => ({ ...f, managementType: e.target.value }))}
                style={inputStyle}
              >
                <MgmtSelectOptions />
              </select>
            </div>
            <div>
              <label style={labelStyle}>Area operativa</label>
              <select
                value={editingForm.operationalArea}
                onChange={(e) => setEditingForm((f) => ({ ...f, operationalArea: e.target.value }))}
                style={inputStyle}
              >
                <AreaSelectOptionsWithBlank />
              </select>
            </div>

            {/* Co-operatori multi-select (28/02/2026) */}
            <div className="pq-col-full">
              <label style={labelStyle}>Co-operatori (opzionale)</label>
              <CoOperatorsPicker
                value={editingForm.coOperatorIds || []}
                operators={operators}
                excludeId={editingForm.creatorOperatorId}
                onChange={handleEditCoOpChange}
              />
            </div>

            {/* Descrizione attività in corso */}
            <div className="pq-col-full">
              <label style={labelStyle}>Descrizione attività</label>
              <textarea
                value={editingForm.description}
                onChange={(e) => setEditingForm((f) => ({ ...f, description: e.target.value }))}
                style={{ ...inputStyle, minHeight: 90, fontFamily: "inherit", resize: "vertical" }}
                placeholder="Descrivi l'attività operativa in corso..."
                data-testid="pq-modal-description"
              />
            </div>

            {/* Bolli / Spese (28/02/2026) */}
            <div className="pq-col-full">
              <label style={labelStyle}>Bolli / Spese</label>
              <ExpensesRowsEditor
                rows={editingForm.expensesList || []}
                onChange={(rows) => setEditingForm((f) => ({ ...f, expensesList: rows }))}
              />
            </div>

            {/* Note Aggiuntive Operative — append-only (28/02/2026).
                Visibile solo in MODIFICA (record già esistente). */}
            {editingItem?.id && (
              <div className="pq-col-full">
                <label style={labelStyle}>Note aggiuntive operative</label>
                <OperationalNotesSection
                  practiceId={editingItem.id}
                  notes={editingItem.operationalNotes || []}
                  onAdded={(updatedPractice) => {
                    setItems((prev) => prev.map((x) => (x.id === updatedPractice.id ? updatedPractice : x)));
                    setEditingItem(updatedPractice);
                  }}
                />
              </div>
            )}

            {/* Messaggio workflow CORRETTO (28/02/2026):
                rimosso "nasce aperto", "Concluso" non è completamento. */}
            <div className="pq-col-full" style={{ fontSize: 11, color: "#64748b", marginTop: -4, lineHeight: 1.5, padding: "8px 10px", background: "#f8fafc", borderLeft: "3px solid #cbd5e1", borderRadius: 4 }}>
              <strong>Workflow operativo:</strong> stai registrando un&apos;attività operativa in corso.
              Eventuali dettagli possono essere aggiunti in seguito tramite le note operative.
              La spunta di <em>chiusura</em> rappresenta una revisione amministrativa/contabile
              (es. verifica fatturazione), <strong>non</strong> un completamento del task.
            </div>
          </div>
        )}
      </InlineModal>

      <InlineModal
        open={!!noteTarget}
        onClose={closeNoteModal}
        title={`Nota rapida — ${noteTarget?.clientName || ""}`}
        footer={
          <>
            <button type="button" onClick={closeNoteModal} style={btnSecondary}>Annulla</button>
            <button type="button" onClick={saveNote} style={btnPrimary} data-testid="pq-save-note-btn">Aggiungi nota</button>
          </>
        }
      >
        <textarea
          autoFocus
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          style={{ ...inputStyle, minHeight: 100, fontFamily: "inherit", resize: "vertical" }}
          placeholder="Scrivi la nota…"
          data-testid="pq-note-text"
        />
      </InlineModal>

      <InlineModal
        open={!!closeTarget}
        onClose={closeCloseModal}
        title={`Chiudi lavoro — ${closeTarget?.clientName || ""}`}
        footer={
          <>
            <button type="button" onClick={closeCloseModal} style={btnSecondary}>Annulla</button>
            <button type="button" onClick={confirmClose} style={btnPrimary} data-testid="pq-confirm-close-btn">Conferma chiusura</button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <label style={labelStyle}>Data chiusura</label>
            <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} style={inputStyle} data-testid="pq-close-date" />
          </div>
          <div>
            <label style={labelStyle}>Note di chiusura (opzionale)</label>
            <textarea value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit", resize: "vertical" }} data-testid="pq-close-notes" />
          </div>
        </div>
      </InlineModal>
    </div>
  );
}
